import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { contactAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Mail, Phone, MapPin, CheckCircle2, Loader2 } from 'lucide-react';

const ContactPage = () => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await contactAPI.submit(formData);
      setSubmitted(true);
    } catch (err) {
      setError('Error al enviar el mensaje. Por favor intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <section className="section-spacing border-b border-border">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="caption mb-4">Contacto</p>
            <h1 className="heading-1 mb-6">Hablemos</h1>
            <p className="body-lg">
              ¿Tiene preguntas sobre nuestras oportunidades de inversión o desea aportar un inmueble? 
              Estamos aquí para ayudarle.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="card-premium text-center py-12">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-emerald-500" strokeWidth={1} />
                  <h2 className="heading-3 mb-4">Mensaje Enviado</h2>
                  <p className="body-text mb-6">
                    Gracias por contactarnos. Nuestro equipo revisará su mensaje y responderá a la brevedad.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                    }}
                  >
                    Enviar Otro Mensaje
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre Completo *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Juan Pérez"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Correo Electrónico *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="nombre@empresa.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+52 55 1234 5678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Asunto *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Consulta sobre inversión"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mensaje *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Cuéntenos cómo podemos ayudarle..."
                      rows={6}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary w-full md:w-auto"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Enviar Mensaje'
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="heading-3 mb-6">Información de Contacto</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium mb-1">Correo Electrónico</p>
                      <a href="mailto:info@silverbrick.mx" className="text-muted-foreground hover:text-foreground transition-colors">
                        info@silverbrick.mx
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium mb-1">Teléfono</p>
                      <a href="tel:+525512345678" className="text-muted-foreground hover:text-foreground transition-colors">
                        +52 55 1234 5678
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="h-5 w-5 text-primary mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium mb-1">Oficinas</p>
                      <p className="text-muted-foreground">
                        Paseo de la Reforma 250, Piso 15<br />
                        Col. Juárez, Cuauhtémoc<br />
                        Ciudad de México, 06600
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-premium">
                <h3 className="font-medium mb-4">Horario de Atención</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Lunes - Viernes</span>
                    <span>9:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sábado</span>
                    <span>Solo con cita</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Domingo</span>
                    <span>Cerrado</span>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-muted/50 rounded-sm">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Nota:</strong> Para consultas sobre oportunidades de inversión específicas, 
                  le recomendamos registrarse en nuestra plataforma para acceder a información detallada y documentación.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
